import React, { useState, useEffect } from 'react';
import './Header.css';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getTranslation } from '../data/translations';
import LanguageSelector from './LanguageSelector';

export default function Header({ province = 'Alberta' }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { language } = useLanguage();

  // Close menu when route changes
  useEffect(() => {
    setOpen(false);
  }, [location]);

  // Handle scroll for header shadow
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (open && !e.target.closest('.main-nav') && !e.target.closest('.hamburger')) {
        setOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [open]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  const handleLogout = () => {
    logout();
    setOpen(false);
    navigate('/');
  };

  return (
    <header className={`site-header ${scrolled ? 'scrolled' : ''}`}>
      <div className="container">
        <Link to="/" className="brand-link" onClick={() => setOpen(false)}>
          <div className="brand">
            <span className="brand-icon">🚗</span>
            <span className="brand-text">Test2Pass</span>
          </div>
        </Link>

        <button 
          className={`hamburger ${open ? 'active' : ''}`} 
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen(o => !o)}
        >
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>

        <nav className={`main-nav ${open ? 'open' : ''}`}>
          <div className="nav-links">
            <Link to="/" className="nav-link">
              <span className="nav-icon">🏠</span>
              <span>{getTranslation(language, 'home')}</span>
            </Link>
            <Link to="/practice" className="nav-link">
              <span className="nav-icon">📝</span>
              <span>{getTranslation(language, 'practice')}</span>
            </Link>
            <Link to="/mock-test" className="nav-link">
              <span className="nav-icon">🎯</span>
              <span>{getTranslation(language, 'mockTests')}</span>
            </Link>
            
            {user && (
              <Link to="/dashboard" className="nav-link">
                <span className="nav-icon">📊</span>
                <span>{getTranslation(language, 'dashboard')}</span>
              </Link>
            )}

            {user && (
              <Link to="/learning" className="nav-link">
                <span className="nav-icon">📚</span>
                <span>{getTranslation(language, 'learning')}</span>
              </Link>
            )}
          </div>

          <div className="nav-actions">
            <LanguageSelector />
            {user ? (
              <>
                {/* <div className="user-info">
                  <span className="user-icon">👤</span>
                  <span className="user-name">{user.username}</span>
                  {user.subscription === 'premium' && (
                    <span className="premium-badge">⭐</span>
                  )}
                </div> */}
                <button onClick={handleLogout} className="btn-logout">
                  {getTranslation(language, 'logout')}
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="btn-login">
                  {getTranslation(language, 'login')}
                </Link>
                <Link to="/register" className="btn-signup">
                  {getTranslation(language, 'register')}
                </Link>
              </>
            )}
          </div>
        </nav>

        {/* Mobile overlay */}
        {open && <div className="nav-overlay" onClick={() => setOpen(false)}></div>}
      </div>
    </header>
  );
}
